import {
  getAuth,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  setPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import type { Auth, User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useFirebaseApp } from "./useFirebaseApp";
import { IAuth } from "@kunduz/data-access-auth-api";
import { useSessionStorage } from "react-storage-complete";

export function useFirebaseAuth(): {
  user?: User;
  signOutFromFirebase: () => Promise<void>;
  signInWithGoogle: () => Promise<IAuth.IVerifyOtpResponse | undefined>;
  signInWithFacebook: () => Promise<IAuth.IVerifyOtpResponse | undefined>;
  signInWithApple: () => Promise<IAuth.IVerifyOtpResponse | undefined>;
  resetFirebaseAuth: () => void;
  providerName?: string;
  setProviderName: (_s: string) => void;
} {
  const { app } = useFirebaseApp();
  const user = useRef<User>();
  const [providerName, setProviderName] = useState<string>();

  const [idToken, setIdToken] = useSessionStorage<string>("idToken");

  // initialize the auth
  useEffect(() => {
    if (!app) return;
    const _auth = getAuth(app);
    setPersistence(_auth, browserSessionPersistence);
  }, [app]);

  // listen to on change events of firebase auth sdk
  useEffect(() => {
    if (!app) return;
    const _auth = getAuth(app);

    onAuthStateChanged(_auth, (_user) => {
      if (_user) {
        user.current = _user;
        // setFirebaseUser(_user);
      }
    });
  }, [app]);

  /**
   * sign in with the provider, common logic
   * @param provider
   */
  async function signInWithProvider(
    provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider
  ): Promise<IAuth.IVerifyOtpResponse | undefined> {
    const _auth = getAuth(app);
    const result = await signInWithPopup(_auth, provider);

    const idToken = await result.user.getIdToken();
    setIdToken(idToken);
    const response = await signInToKunduzWithIdToken(idToken);

    return response;
  }

  /**
   * sign in with google
   */
  async function signInWithGoogle(): Promise<
    IAuth.IVerifyOtpResponse | undefined
  > {
    const provider = new GoogleAuthProvider();
    const otpResponse = await signInWithProvider(provider);
    return otpResponse;
  }

  /**
   * sign in with facebook
   */
  async function signInWithFacebook(): Promise<
    IAuth.IVerifyOtpResponse | undefined
  > {
    const provider = new FacebookAuthProvider();
    const otpResponse = await signInWithProvider(provider);
    return otpResponse;
  }

  /**
   * sign in with apple
   */
  async function signInWithApple(): Promise<
    IAuth.IVerifyOtpResponse | undefined
  > {
    const provider = new OAuthProvider("apple.com");
    const otpResponse = await signInWithProvider(provider);
    return otpResponse;
  }

  async function signInToKunduzWithIdToken(
    idToken: string
  ): Promise<IAuth.IVerifyOtpResponse> {
    const response = await fetch(`/api/auth/loginWithFirebase/`, {
      method: "POST",
      body: JSON.stringify({ idToken }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    const jsonResponse = await response.json();

    return jsonResponse;
  }

  /**
   * sign out of firebase sdk
   * @returns
   */
  async function signOutFromFirebase() {
    const _auth = getAuth(app);
    await signOut(_auth);
  }

  function resetFirebaseAuth() {
    signOutFromFirebase();
    // setFirebaseUser(undefined);
    setProviderName(undefined);
  }

  return {
    signOutFromFirebase,
    signInWithGoogle,
    signInWithFacebook,
    signInWithApple,
    resetFirebaseAuth,
    providerName,
    setProviderName,
    user: user.current,
  };
}
